import * as React from "react";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Stack,
	Typography,
} from "@mui/material";

import { useAgentsList } from "@/hooks/api/use-agents";
import { usePersonasList } from "@/hooks/api/use-personas";
import { RunMode, useLaunchRun, useMetrics, useScenarios } from "@/hooks/api/use-proving-ground";

const toggle = (list: string[], id: string) =>
	list.includes(id) ? list.filter((value) => value !== id) : [...list, id];

export function LaunchRunDialog({
	open,
	onClose,
	onLaunched,
}: {
	open: boolean;
	onClose: () => void;
	onLaunched: (runId: number) => void;
}): React.JSX.Element {
	const agents = useAgentsList();
	const scenarios = useScenarios();
	const personas = usePersonasList();
	const metrics = useMetrics();
	const launch = useLaunchRun();

	const [agentId, setAgentId] = React.useState("");
	const [mode, setMode] = React.useState<RunMode>("text");
	const [scenarioIds, setScenarioIds] = React.useState<string[]>([]);
	const [personaIds, setPersonaIds] = React.useState<string[]>([]);

	const agentList = agents.data?.data ?? [];

	/**
	 * Selects what suits the chosen agent, so picking one is the whole setup.
	 *
	 * Anything tagged for this agent, plus anything general. General is not a fallback here:
	 * a caller carries no goal of its own, so most of them genuinely work against any agent,
	 * and excluding them would leave a grid one column wide. Situations are the half that
	 * belongs to an agent, so tagging usually narrows those and leaves the callers alone.
	 *
	 * Everything stays tickable. This is a starting point, not a restriction.
	 */
	React.useEffect(() => {
		if (!open) return;
		const suits = (item: { agentId?: string | null }) => !item.agentId || item.agentId === agentId;
		setScenarioIds((scenarios.data ?? []).filter((item) => suits(item)).map((s) => s.id));
		setPersonaIds((personas.data ?? []).filter((item) => suits(item)).map((p) => p.id));
	}, [open, agentId, scenarios.data, personas.data]);

	const cells = scenarioIds.length * personaIds.length;
	const canLaunch = Boolean(agentId) && cells > 0 && (metrics.data?.length ?? 0) > 0;

	const handleLaunch = async () => {
		const run = await launch.mutateAsync({ agentId, mode, scenarioIds, personaIds });
		onClose();
		onLaunched(run.id);
	};

	return (
		<Dialog open={open} onClose={launch.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
			<DialogTitle>Launch a run</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{ mt: 1 }}>
					{metrics.data && metrics.data.length === 0 ? (
						<Alert severity="warning">
							Your scorecard is empty, so every call would score zero out of zero — which reads as a clean sweep. Add at
							least one metric first.
						</Alert>
					) : null}

					{/* An empty required picker with no explanation is indistinguishable from having no
					    agents at all, which is the hardest kind of failure to diagnose from the screen. */}
					{agents.isError ? (
						<Alert severity="error">Could not load your agents. Reload the page, or check the API is reachable.</Alert>
					) : null}
					{!agents.isLoading && !agents.isError && agentList.length === 0 ? (
						<Alert severity="warning">No agents found. Create one before running the Proving Ground.</Alert>
					) : null}

					<FormControl fullWidth required>
						<InputLabel>Agent</InputLabel>
						<Select
							value={agentId}
							label="Agent"
							disabled={agents.isLoading}
							onChange={(event) => setAgentId(event.target.value)}
						>
							{agentList.map((agent: any) => (
								<MenuItem key={agent.id} value={agent.id}>
									{agent.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Mode
						</Typography>
						<RadioGroup value={mode} onChange={(event) => setMode(event.target.value as RunMode)}>
							<FormControlLabel
								value="text"
								control={<Radio />}
								label={
									<Box>
										<Typography variant="body2">Text — fast and cheap</Typography>
										<Typography variant="caption" color="text.secondary">
											Runs with no clock at all, so a full grid finishes in minutes. Tests what the agent says, not how
											it speaks or listens.
										</Typography>
									</Box>
								}
							/>
							<FormControlLabel
								value="voice"
								control={<Radio />}
								label={
									<Box>
										<Typography variant="body2">Voice — the real thing</Typography>
										<Typography variant="caption" color="text.secondary">
											Real time, so a grid takes as long as the calls do. The only mode that can catch interruption,
											mispronunciation or mishearing.
										</Typography>
									</Box>
								}
							/>
						</RadioGroup>
					</Box>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Scenarios ({scenarioIds.length})
						</Typography>
						<Box sx={{ maxHeight: 130, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
							{(scenarios.data ?? []).map((scenario) => (
								<FormControlLabel
									key={scenario.id}
									sx={{ display: "flex" }}
									control={
										<Checkbox
											size="small"
											checked={scenarioIds.includes(scenario.id)}
											onChange={() => setScenarioIds((list) => toggle(list, scenario.id))}
										/>
									}
									label={<Typography variant="body2">{scenario.name}</Typography>}
								/>
							))}
							{scenarios.data?.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									No scenarios yet.
								</Typography>
							) : null}
						</Box>
					</Box>

					<Box>
						<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
							Personas ({personaIds.length})
						</Typography>
						<Box sx={{ maxHeight: 130, overflowY: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 1 }}>
							{(personas.data ?? []).map((persona) => (
								<FormControlLabel
									key={persona.id}
									sx={{ display: "flex" }}
									control={
										<Checkbox
											size="small"
											checked={personaIds.includes(persona.id)}
											onChange={() => setPersonaIds((list) => toggle(list, persona.id))}
										/>
									}
									label={<Typography variant="body2">{persona.name}</Typography>}
								/>
							))}
							{personas.data?.length === 0 ? (
								<Typography variant="body2" color="text.secondary">
									No personas yet.
								</Typography>
							) : null}
						</Box>
					</Box>

					{/* Voice cells run in real time, so the size of the grid is the cost of the run. */}
					<Alert severity="info">
						{cells} {cells === 1 ? "call" : "calls"} will be simulated.
						{mode === "voice" ? " In voice mode each one runs in real time." : ""}
					</Alert>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary" disabled={launch.isPending}>
					Cancel
				</Button>
				<Button onClick={handleLaunch} variant="contained" disabled={!canLaunch || launch.isPending}>
					Launch
				</Button>
			</DialogActions>
		</Dialog>
	);
}
